import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  detailTableActionsConfig,
  detailColumnDefs,
  detailFrameworkComponents
} from '../config/table'

import { ns, USER_FEEDBACK_STORE_KEY } from '../constants'
import { NotificationActions, useStoreSelector } from '@ublique/platform'

import { fetchDetailTableData, updateStatus } from '../actions'
import { userFeedbackStore } from '../state'
import { first, isEmpty, uniqueId } from 'lodash'
import { userSelectors } from '../state'
import useFetchData from 'pages/ExtraOutput/hooks/useFetchData'

const { addNotification } = NotificationActions

export default function useDetailTableConfig({ selectedRow, setKey }) {
  const [, setGridApi] = useState()
  const { t, i18n } = useTranslation('ExtraOutput')
  const translate = (n) => t(`${ns}.${n}`)
  const isEditable = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    userSelectors.getIsEditable
  )
  const detailSelectedRow = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    userSelectors.getSelectedRow
  )
  const uom = useStoreSelector(USER_FEEDBACK_STORE_KEY, userSelectors.getUom)
  const pageSize = useStoreSelector(
    USER_FEEDBACK_STORE_KEY,
    userSelectors.getDetailPageSize
  )

  const fetchParams = useMemo(() => ({ selectedRow, uom }), [selectedRow, uom])

  const { data, refreshData } = useFetchData(fetchDetailTableData, fetchParams)

  const store = useStoreSelector(
    'storeCollaboration',
    (state) => state.filters.store
  )

  const onRowSelected = (rows) => {
    const row = first(rows)
    userFeedbackStore.update((prevState) => ({
      ...prevState,
      compositionTable: {
        ...prevState.compositionTable,
        selectedRow: !isEmpty(rows) ? row : null
      }
    }))
  }

  const selection = {
    rowSelection: 'single',
    onSelectionChanged: onRowSelected
  }

  const onPaginationSizeChange = (size) => {
    userFeedbackStore.update((prevState) => ({
      ...prevState,
      compositionTable: {
        ...prevState.compositionTable,
        pageSize: size,
        selectedRow: null
      }
    }))
  }

  const changeStatus = (action) => {
    const payload = {
      id: detailSelectedRow.id,
      status: detailSelectedRow.status,
      action
    }
    updateStatus(store, 'stock', payload).then((res) => {
      if (isEmpty(res)) {
        addNotification({
          message: t('EDIT_SUCCESS'),
          kind: 'success'
        })
        refreshData()
        setKey(uniqueId())
      }
    })
  }

  const extraHeaderComponent = {
    extraHeaderComponentContainerClassName: 'extraComponent',
    extraHeaderComponent: {
      t,
      config: detailTableActionsConfig({
        t: translate,
        onPaginationSizeChange,
        pageSize,
        changeStatus,
        isEditable,
        detailSelectedRow
      })
    }
  }

  const resetState = () => {
    userFeedbackStore.update((prevState) => ({
      ...prevState,
      compositionTable: {
        ...prevState.compositionTable,
        selectedRow: null
      }
    }))
  }

  useEffect(
    () => () => {
      resetState()
    },
    []
  )

  return [
    {
      config: { sm: 4, md: 8, lg: 12 },
      tableProps: {
        key: i18n.language + pageSize,
        resizable: true,
        sortable: true,
        lockPosition: true,
        floatingFilter: true,
        canExportCSV: false,
        suppressContextMenu: true,
        canSearch: false,
        paginationPageSize: 5,
        pagination: true,
        onGridReady: setGridApi,
        cellEditorPopup: true,
        rowData: data,
        frameworkComponents: detailFrameworkComponents,
        columnDefs: detailColumnDefs({ t: translate, isEditable }),
        ...extraHeaderComponent,
        ...selection
      }
    }
  ]
}
